import { useState, useCallback, useEffect } from 'react';
import { Bell, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.slice(0, 10));
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} style={{ color: unreadCount > 0 ? '#D4AF37' : '#666' }} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold"
            style={{ background: '#D4AF37', color: '#000' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div 
          className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto rounded-xl shadow-lg z-50"
          style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
        >
          <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}>
            <span className="font-semibold">Notifications</span>
            {unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="text-xs hover:underline"
                style={{ color: '#D4AF37' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">
              No notifications yet
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              {notifications.map((notif) => (
                <div 
                  key={notif.id}
                  className={`p-3 hover:bg-white/5 cursor-pointer transition-colors ${!notif.read ? 'bg-white/5' : ''}`}
                  onClick={() => markAsRead(notif.id)}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: notif.read ? '#888' : '#fff' }}>
                        {notif.message}
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#666' }}>
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full mt-2" style={{ background: '#D4AF37' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
